import React, { useState, useEffect } from "react";
import { collection, onSnapshot, addDoc, updateDoc, doc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, auth, storage } from "../../firebaseConfig";
import toast from "react-hot-toast";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { EffectFade, Pagination, Navigation, Autoplay } from "swiper/modules";

function Story() {
  const [stories, setStories] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [userClaims, setUserClaims] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [newStory, setNewStory] = useState({
    title: "",
    img: "",
    tarih: "",
    startDate: "",
    endDate: "",
    goruntulenme: 0,
    like: 0,
  });

  // Kullanıcı yetkilerini kontrol et
  useEffect(() => {
    const checkUserClaims = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          toast.error("Kullanıcı oturum açmamış. Yönlendiriliyorsunuz...");
          setTimeout(() => {
            window.location.href = "/login";
          }, 3000);
          return;
        }
        const idTokenResult = await user.getIdTokenResult();
        const claims = idTokenResult.claims;
        setUserClaims(claims);
      } catch (error) {
        console.error("Yetki kontrolü yapılırken hata oluştu:", error);
        toast.error("Yetki kontrolü başarısız.");
      } finally {
        setLoading(false);
      }
    };

    checkUserClaims();
  }, []);

  // Firestore'dan story verilerini gerçek zamanlı izleme
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "buyuksehir_story"),
      (snapshot) => {
        const storiesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setStories(storiesData);
        if (storiesData.length > 0) {
          setSelectedStory(storiesData[0]); // İlk hikayeyi varsayılan olarak seç
        }
      },
      (error) => {
        console.error("Veriler gerçek zamanlı izlenirken hata oluştu:", error);
        toast.error("Veriler izlenirken hata oluştu.");
      }
    );

    return () => unsubscribe();
 

}, []);

// Görsel seçme işlemi
const handleFileChange = (event) => {
  const file = event.target.files[0];
  if (file) {
    if (!file.type.startsWith("image/")) {
      toast.error("Lütfen geçerli bir görsel dosyası seçin.");
      return;
    }
    setImage(file);
    setPreview(URL.createObjectURL(file));
  } else {
    toast.error("Dosya seçilmedi.");
  }
};

// Story güncelleme işlemi
const handleUpdateStory = async () => {
  if (!selectedStory || !selectedStory.id) {
    toast.error("Güncellenecek bir story seçilmedi.");
    return;
  }

  try {
    let updatedData = { ...selectedStory };

    // Yeni görsel seçildiyse yükleme işlemi
    if (image) {
      const storageRef = ref(storage, `story-images/${image.name}`);
      await uploadBytes(storageRef, image);
      const downloadURL = await getDownloadURL(storageRef);
      updatedData.img = downloadURL;
    }

    // Firestore'da dokümanı güncelle
    await updateDoc(doc(db, "buyuksehir_story", selectedStory.id), {
      ...updatedData,
      img: updatedData.img,
      tarih: updatedData.tarih, // Günlük, Haftalık, Aylık seçimi
      title: updatedData.title, // Başlık güncelleme
    });

    toast.success("Story başarıyla güncellendi.");
    setImage(null); // Görsel state sıfırla
    setPreview(null);
  } catch (error) {
    console.error("Story güncellenirken hata oluştu:", error);
    toast.error("Story güncellenirken hata oluştu.");
  }
};

// Yeni story oluşturma işlemi
const handleAddStory = async () => {
  if (!userClaims?.is_admin && !userClaims?.is_editor) {
    toast.error("Bu işlemi yapmak için yetkiniz yok.");
    return;
  }

  if (!image || !newStory.tarih || !newStory.title) {
    toast.error("Lütfen tüm alanları doldurun.");
    return;
  }

  try {
    const storageRef = ref(storage, `story-images/${image.name}`);
    await uploadBytes(storageRef, image);
    const downloadURL = await getDownloadURL(storageRef);

    const storyData = {
      ...newStory,
      img: downloadURL,
      goruntulenme: 0,
      like: 0,
    };

    await addDoc(collection(db, "buyuksehir_story"), storyData);

    toast.success("Yeni story başarıyla oluşturuldu.");
    setNewStory({ title: "", img: "", tarih: "", goruntulenme: 0, like: 0 });
    setImage(null);
    setPreview(null);
    setIsPopupOpen(false);
  } catch (error) {
    console.error("Story eklenirken hata oluştu:", error);
    toast.error("Story eklenirken hata oluştu.");
  }
};

if (loading) {
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
    </div>
  );
}

return (
  <div className="p-6 max-w-5xl mx-auto relative">
    {/* Yeni Story Oluşturma Popup */}
    {isPopupOpen && (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      >
        <div className="bg-white p-6 rounded shadow-lg w-96">
          <h2 className="text-xl font-bold mb-4">Yeni Story Oluştur</h2>
          <div className="space-y-4">
            <label>
              Başlık:
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-lg"
                value={newStory.title}
                onChange={(e) => setNewStory({ ...newStory, title: e.target.value })}
              />
            </label>
            <label>
              Yayın Süresi:
              <select
                className="w-full p-2 border border-gray-300 rounded-lg"
                value={newStory.tarih}
                onChange={(e) => setNewStory({ ...newStory, tarih: e.target.value })}
              >
                <option value="">Seçiniz</option>
                <option value="Günlük">Günlük</option>
                <option value="Haftalık">Haftalık</option>
                <option value="Aylık">Aylık</option>
              </select>
            </label>
            {preview && (
              <div className="relative">
                <img
                  src={preview}
                  alt="Preview"
                  className="w-full h-48 object-cover rounded-md mb-4"
                />
              </div>
            )}
            <label htmlFor="fileInput" className="block w-full p-2 border border-dashed rounded cursor-pointer">
              Görsel Yükle
              <input
                id="fileInput"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
            <button
              className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
              onClick={handleAddStory}
            >
              Oluştur
            </button>
          </div>
          <button
            className="w-full mt-4 text-center text-red-600"
            onClick={() => setIsPopupOpen(false)}
          >
            İptal
          </button>
        </div>
      </div>
    )}

    {/* Sağ Üst Popup Butonu */}
    <button
      className="absolute top-6 right-6 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
      onClick={() => setIsPopupOpen(true)}
    >
      Yeni Story
    </button>

    {/* Story Güncelleme ve Görünüm */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Güncellenebilir Story */}
      <div className="bg-white p-6 rounded shadow">
        <h1 className="text-2xl font-bold mb-4">Story Güncelle</h1>
        {selectedStory ? (
          <div className="space-y-4">
            <label>
              Başlık:
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-lg"
                value={selectedStory.title}
                onChange={(e) =>
                  setSelectedStory({ ...selectedStory, title: e.target.value })
                }
              />
            </label>
            <label>
              Yayın Süresi:
              <select
                className="w-full p-2 border border-gray-300 rounded-lg"
                value={selectedStory.tarih}
                onChange={(e) =>
                  setSelectedStory({ ...selectedStory, tarih: e.target.value })
                }
              >
                <option value="">Seçiniz</option>
                <option value="Günlük">Günlük</option>
                <option value="Haftalık">Haftalık</option>
                <option value="Aylık">Aylık</option>
              </select>
            </label>

            <p>Görüntülenme: {selectedStory.goruntulenme}</p>
            <p>Beğeni: {selectedStory.like}</p>
            {selectedStory.img && (
              <img
                src={selectedStory.img}
                alt="Selected Story"
                className="w-full h-48 object-cover rounded-md mb-4"
              />
            )}
            <label htmlFor="fileUpdate" className="block w-full p-2 border border-dashed rounded cursor-pointer">
              Görsel Güncelle
              <input
                id="fileUpdate"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
            <button
              className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700"
              onClick={handleUpdateStory}
            >
              Güncelle
            </button>
          </div>
        ) : (
          <p className="text-gray-500">Story seçilmedi.</p>
        )}
      </div>

      {/* Hikaye Slider */}
      <div className="bg-gray-100 p-6 rounded shadow">
        <h2 className="text-xl font-bold mb-4 text-center">Hikaye Görünümü</h2>
        <Swiper
          modules={[EffectFade, Pagination, Navigation, Autoplay]}
          effect="fade"
          pagination={{ clickable: true }}
          navigation
          autoplay={{ delay: 5000 }}
          className="rounded shadow-lg"
          onSlideChange={(swiper) =>
            setSelectedStory(stories[swiper.activeIndex] || null)
          }
        >
          {stories.map((story) => (
            <SwiperSlide key={story.id}>
              <div className="relative bg-black rounded-[50px] overflow-hidden border-8 border-black shadow-lg w-full aspect-[9/16]">
                <div className="absolute inset-0 bg-white rounded-[45px]">
                  <img
                    src={story.img}
                    alt={story.title}
                    className="w-full h-full object-cover rounded-md"
                  />
                </div>
                <div className="absolute bottom-4 left-4 text-white bg-black bg-opacity-60 px-2 py-1 rounded">
                  <p className="text-sm font-bold">{story.title}</p>
                  <p className="text-xs">Beğeni: {story.like}</p>
                  <p className="text-xs">Görüntülenme: {story.goruntulenme}</p>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  </div>
);
}

export default Story;
